import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  Paper,
  Stack,
  TextField,
  Autocomplete,
  Typography,
  Chip,
  Divider,
  Checkbox,
  Button,
  CircularProgress,
  IconButton,
} from "@mui/material";

import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";

import { MaterialReactTable } from "material-react-table";

import { ComparisonTablePlaceholder } from "./TablePlaceholder";
import TreeViewDrawer from "../../../../../../../components/Drawers/TreeViewDrawer";
import useEmsProjectsTreeData from "../../../../../../../components/Drawers/TreeViewDrawer/useEmsProjectsTreeData";
import EmsProjectsTreeView from "../../../../../../../components/Drawers/TreeViewDrawer/EmsProjectsTreeView";
import {
  fetchSideMaterials,
  sideKey,
} from "./compareMaterials";

const auditMetricOptions = [
  "Quantity",
  "Dimension",
  "Cost",
  "Schedule",
  "Weight",
];

const metricColors = {
  Quantity: "#e8f5e9",
  Dimension: "#fff3e0",
  Cost: "#e3f2fd",
  Schedule: "#fce4ec",
  Weight: "#f3e5f5",
};

// ─────────────────────────────────────────────────────────────
// Helpers for normalized compare rows (see compareMaterials.js):
// { key, uid, description, quantity, dimension, weight, cost, schedule }
// ─────────────────────────────────────────────────────────────

const toNum = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

// Audit metric -> field on a normalized row.
const METRIC_FIELDS = {
  Quantity: "quantity",
  Dimension: "dimension",
  Weight: "weight",
  Cost: "cost",
  Schedule: "schedule",
};

const isEmpty = (v) => v === null || v === undefined || v === "";

// Variance between two raw values: numeric difference, Matched/Mismatch
// for text, null when either side is missing.
const varianceOf = (lv, rv) => {
  if (isEmpty(lv) || isEmpty(rv)) return null;
  const na = toNum(lv);
  const nb = toNum(rv);
  if (na !== null && nb !== null) return nb - na;
  return String(lv) === String(rv) ? "Matched" : "Mismatch";
};

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

const formatSelectionLabel = (sel) => {
  if (!sel) return "";
  // 2D Drawing pick: { kind: "drawing", projectName, dwgNumber, revision }
  if (sel.kind === "drawing") {
    const rev = sel.revision ? ` (Rev ${sel.revision})` : "";
    return (
      [sel.projectName, `${sel.dwgNumber || "Untitled drawing"}${rev}`]
        .filter(Boolean)
        .join(" / ") || "Untitled"
    );
  }
  // Estimation pick: { kind: "pv", projectName, revision, subTabLabel/topTabLabel }
  if (sel.kind === "pv") {
    const scope = sel.topTabLabel || "Estimation";
    const rev = sel.revision ? `Rev ${sel.revision}` : "";
    return (
      [sel.projectName, `${scope} ${rev}`.trim()]
        .filter(Boolean)
        .join(" / ") || "Untitled"
    );
  }
  const parts = [
    sel.plant?.name,
    sel.wbs?.name || sel.wbs?.projectName,
    sel.doc?.documentName || sel.doc?.uid || sel.dwgNumber || "",
  ].filter(Boolean);
  return parts.join(" / ") || "Untitled";
};

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export default function MSelectionBar() {
  const [leftSelection, setLeftSelection] = useState(null);
  const [rightSelection, setRightSelection] = useState(null);

  const [pickerSide, setPickerSide] = useState(null); // "left" | "right" | null

  const [auditMetrics, setAuditMetrics] = useState([]);

  // Materials of each picked drawing (via GET drawing/:id -> .materials).
  const [materialsLeft, setMaterialsLeft] = useState(null);
  const [materialsRight, setMaterialsRight] = useState(null);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [materialsError, setMaterialsError] = useState("");
  const [materialsRetry, setMaterialsRetry] = useState(0);

  const treeData = useEmsProjectsTreeData();

  // ─────────────────────────────────────────
  // Ready State
  // ─────────────────────────────────────────

  const leftKey = sideKey(leftSelection);
  const rightKey = sideKey(rightSelection);
  const sidesReady = Boolean(leftKey && rightKey);
  const materialsReady =
    sidesReady &&
    !materialsLoading &&
    !materialsError &&
    Array.isArray(materialsLeft) &&
    Array.isArray(materialsRight);

  // ─────────────────────────────────────────
  // Load materials once both drawings are picked
  // ─────────────────────────────────────────

  useEffect(() => {
    setMaterialsLeft(null);
    setMaterialsRight(null);
    setMaterialsError("");

    if (!leftKey || !rightKey) {
      setMaterialsLoading(false);
      return;
    }

    let cancelled = false;
    setMaterialsLoading(true);

    Promise.all([
      fetchSideMaterials(leftSelection),
      fetchSideMaterials(rightSelection),
    ])
      .then(([l, r]) => {
        if (cancelled) return;
        setMaterialsLeft(Array.isArray(l) ? l : []);
        setMaterialsRight(Array.isArray(r) ? r : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setMaterialsError(
          err?.response?.data?.message || "Failed to load side materials",
        );
      })
      .finally(() => {
        if (!cancelled) setMaterialsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftKey, rightKey, materialsRetry]);

  // ─────────────────────────────────────────
  // Default audit matrix once both drawings' materials are in
  // (applied once per drawing pair — the user can still change it)
  // ─────────────────────────────────────────

  const appliedDefaultsRef = useRef("");
  useEffect(() => {
    const key = `${leftKey}|${rightKey}`;
    if (materialsReady && appliedDefaultsRef.current !== key) {
      appliedDefaultsRef.current = key;
      setAuditMetrics(["Quantity", "Cost", "Schedule"]);
    }
  }, [materialsReady, leftKey, rightKey]);

  // ─────────────────────────────────────────
  // Comparison rows from the two material lists
  // ─────────────────────────────────────────

  const comparisonRows = useMemo(() => {
    if (!Array.isArray(materialsLeft) || !Array.isArray(materialsRight)) {
      return [];
    }
    const byKey = new Map();
    const push = (m, side) => {
      const key = m?.key || "row";
      if (!byKey.has(key)) byKey.set(key, { left: null, right: null });
      byKey.get(key)[side] = m;
    };
    materialsLeft.forEach((m) => push(m, "left"));
    materialsRight.forEach((m) => push(m, "right"));

    return [...byKey.entries()].map(([key, { left, right }]) => {
      const ref = left ?? right;

      let status;
      if (!left) {
        status = "Missing In A";
      } else if (!right) {
        status = "Missing In B";
      } else {
        const differs =
          String(left.description ?? "") !==
            String(right.description ?? "") ||
          Object.values(METRIC_FIELDS).some(
            (field) =>
              String(left[field] ?? "") !== String(right[field] ?? ""),
          );
        status = differs ? "Variance Found" : "Matched";
      }

      const row = {
        id: key,
        semanticId: ref?.uid || key,
        component: ref?.description || "Untitled",
        status,
      };

      Object.entries(METRIC_FIELDS).forEach(([metric, field]) => {
        const lv = left ? (left[field] ?? null) : null;
        const rv = right ? (right[field] ?? null) : null;
        row[metric] = { left: lv, right: rv, variance: varianceOf(lv, rv) };
      });

      return row;
    });
  }, [materialsLeft, materialsRight]);

  // ─────────────────────────────────────────
  // Dynamic Columns
  // ─────────────────────────────────────────

  const columns = useMemo(() => {
    const leftLabel = leftSelection
      ? formatSelectionLabel(leftSelection)
      : "Side A";

    const rightLabel = rightSelection
      ? formatSelectionLabel(rightSelection)
      : "Side B";

    const baseColumns = [
      {
        accessorKey: "semanticId",
        header: "UID",
        size: 180,
        enablePinning: true,
      },

      {
        accessorKey: "component",
        header: "Component",
        size: 250,
      },

      // {
      //   accessorKey: "status",
      //   header: "Status",
      //   size: 160,
      //   muiTableHeadCellProps: {
      //     align: "center",
      //   },
      //   muiTableBodyCellProps: {
      //     align: "center",
      //   },
      //   Cell: ({ cell }) => {
      //     const value = cell.getValue();

      //     const statusConfig = {
      //       Matched: {
      //         color: "success",
      //         variant: "filled",
      //       },

      //       "Variance Found": {
      //         color: "warning",
      //         variant: "filled",
      //       },

      //       "Major Variance": {
      //         color: "error",
      //         variant: "filled",
      //       },

      //       "Critical Variance": {
      //         color: "error",
      //         variant: "filled",
      //       },

      //       "Missing In A": {
      //         color: "info",
      //         variant: "outlined",
      //       },

      //       "Missing In B": {
      //         color: "info",
      //         variant: "outlined",
      //       },

      //       Pending: {
      //         color: "default",
      //         variant: "outlined",
      //       },
      //     };

      //     const config = statusConfig[value] || {
      //       color: "default",
      //       variant: "outlined",
      //     };

      //     return (
      //       <Chip
      //         size="small"
      //         label={value}
      //         color={config.color}
      //         variant={config.variant}
      //         sx={{
      //           fontWeight: 700,
      //           minWidth: 120,
      //         }}
      //       />
      //     );
      //   },
      // },
    ];

    const subColumnStyle = ({ metric }) => ({
      grow: false,
      size: 80,
      muiTableHeadCellProps: {
        align: "center",
        sx: {
          bgcolor: metricColors[metric],
        },
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          bgcolor: metricColors[metric],
        },
      },
    });

    const metricColumns = auditMetrics.map((metric) => {
      return {
        id: metric,

        header: metric,

        muiTableHeadCellProps: {
          sx: {
            bgcolor: metricColors[metric],
            fontWeight: 800,
            textAlign: "center",
            borderLeft: "1px solid",
            borderColor: "divider",
          },
        },

        columns: [
          {
            accessorKey: `${metric}.left`,
            header: leftLabel,

            size: 140,

            ...subColumnStyle({ metric }),

            Cell: ({ row }) => {
              const value = row.original?.[metric]?.left;

              return (
                <Box
                  sx={{
                    fontWeight: 500,
                  }}
                >
                  {value ?? "-"}
                </Box>
              );
            },
          },

          {
            accessorKey: `${metric}.right`,
            header: rightLabel,

            size: 140,

            ...subColumnStyle({ metric }),

            Cell: ({ row }) => {
              const value = row.original?.[metric]?.right;

              return (
                <Box
                  sx={{
                    fontWeight: 500,
                  }}
                >
                  {value ?? "-"}
                </Box>
              );
            },
          },

          {
            accessorKey: `${metric}.delta`,
            header: "\u0394",

            size: 110,

            ...subColumnStyle({ metric }),

            Cell: ({ row }) => {
              const delta = row.original?.[metric]?.variance;

              if (delta === null || delta === undefined) {
                return "-";
              }

              if (typeof delta === "number") {
                return (
                  <Box
                    sx={{
                      fontWeight: 700,
                      color:
                        delta > 0
                          ? "success.main"
                          : delta < 0
                            ? "error.main"
                            : "text.secondary",
                    }}
                  >
                    {delta > 0 ? `+${delta}` : delta}
                  </Box>
                );
              }

              return (
                <Chip
                  label={delta}
                  size="small"
                  color={
                    delta === "Matched"
                      ? "success"
                      : delta === "Mismatch" || delta === "Critical"
                        ? "error"
                        : "warning"
                  }
                  variant="outlined"
                />
              );
            },
          },
        ],
      };
    });

    return [...baseColumns, ...metricColumns];
  }, [auditMetrics, leftSelection, rightSelection]);

  // ─────────────────────────────────────────
  // Picker Callbacks
  // ─────────────────────────────────────────

  const handlePickerSelect = (target) => {
    if (pickerSide === "left") {
      setLeftSelection(target);
    } else if (pickerSide === "right") {
      setRightSelection(target);
    }
    setPickerSide(null);
  };

  // ─────────────────────────────────────────
  // Render Selection Side
  // ─────────────────────────────────────────

  const renderSelectionSide = (title, selection, side) => {
    return (
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          p: 2,
          borderRadius: 3,
          border: "1px solid",
          borderColor: selection ? "primary.light" : "divider",
          bgcolor: "background.default",
          transition: "border-color 0.2s ease",
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CompareArrowsIcon color="primary" fontSize="small" />
            <Typography fontWeight={700}>{title}</Typography>
          </Stack>

          {selection ? (
            <Stack spacing={1}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "action.hover",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <DescriptionIcon color="primary" fontSize="small" />
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatSelectionLabel(selection)}
                </Typography>
                <IconButton
                  size="small"
                  title="Change selection"
                  onClick={() => setPickerSide(side)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          ) : (
            <Button
              variant="outlined"
              startIcon={<AccountTreeIcon />}
              onClick={() => setPickerSide(side)}
              sx={{
                py: 2,
                borderStyle: "dashed",
                textTransform: "none",
              }}
            >
              Select Source
            </Button>
          )}
        </Stack>
      </Paper>
    );
  };

  return (
    <>
      {/* ───────────────────────────────────────── */}
      {/* Tree Picker Panel (fixed to absolute left of the screen) */}
      {/* ───────────────────────────────────────── */}

      <TreeViewDrawer open={Boolean(pickerSide)} anchor="left" overlay>
        <Box
          sx={{
            height: "100%",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              {pickerSide === "left"
                ? "Select Side A (Drawing / Estimation)"
                : "Select Side B (Drawing / Estimation)"}
            </Typography>
            <IconButton size="small" onClick={() => setPickerSide(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            <EmsProjectsTreeView
              data={treeData}
              activePlantId={undefined}
              onSelect={handlePickerSelect}
              pickerMode
            />
          </Box>
        </Box>
      </TreeViewDrawer>

      {/* ───────────────────────────────────────── */}
      {/* Main Content */}
      {/* ───────────────────────────────────────── */}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Selection Bar */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 4,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack spacing={2.5}>
            {/* Two Comparison Sides */}
            <Stack
              direction={{
                xs: "column",
                xl: "row",
              }}
              spacing={2}
            >
              {renderSelectionSide("Comparison Side A", leftSelection, "left")}
              {renderSelectionSide(
                "Comparison Side B",
                rightSelection,
                "right",
              )}
            </Stack>

            {/* Metrics (matrix) — shown once both drawings' materials load */}
            {materialsReady && (
              <>
                <Divider />

                <Stack
                  direction={{
                    xs: "column",
                    lg: "row",
                  }}
                  spacing={2}
                  justifyContent="center"
                >
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    size="small"
                    options={auditMetricOptions}
                    value={auditMetrics}
                    onChange={(e, value) => setAuditMetrics(value)}
                    sx={{ minWidth: 500 }}
                    getOptionLabel={(option) => option}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox checked={selected} size="small" />
                        {option}
                      </li>
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          size="small"
                          {...getTagProps({
                            index,
                          })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Audit Metrics"
                        placeholder="Select metrics"
                      />
                    )}
                  />
                </Stack>
              </>
            )}
          </Stack>
        </Paper>

        {/* Table */}
        {!sidesReady ? (
          <ComparisonTablePlaceholder />
        ) : materialsLoading ? (
          <Paper
            elevation={0}
            sx={{
              minHeight: 420,
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 4,
            }}
          >
            <Stack spacing={2} alignItems="center">
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary">
                Loading side materials…
              </Typography>
            </Stack>
          </Paper>
        ) : materialsError ? (
          <Paper
            elevation={0}
            sx={{
              minHeight: 240,
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 4,
            }}
          >
            <Stack spacing={2} alignItems="center">
              <Typography variant="body2" color="error">
                {materialsError}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setMaterialsRetry((n) => n + 1)}
              >
                Retry
              </Button>
            </Stack>
          </Paper>
        ) : (
          <MaterialReactTable
            columns={columns}
            data={comparisonRows}
            enableTopToolbar={false}
            enableColumnActions={false}
            enableSorting={false}
            enableStickyHeader
            enableDensityToggle={false}
            enableFullScreenToggle
            muiTableContainerProps={{
              sx: {
                maxHeight: "65vh",
              },
            }}
            initialState={{
              density: "compact",
              pagination: {
                pageSize: 10,
                pageIndex: 0,
              },
            }}
          />
        )}
      </Box>
    </>
  );
}
